import Button from "@edaz/edazkit/Button";
import { IconType } from "@edaz/edazkit/Icon";
import LoaderOverlay from "@edaz/edazkit/LoaderOverlay";
import type { PropertyName } from "@frui.ts/helpers";
import type { ViewComponent } from "@frui.ts/views/dist";
import { registerView } from "@frui.ts/views/dist";
import type ChargeChange from "entities/chargeChange";
import ChargePropertyChangeDisplayType from "entities/chargePropertyChangeDisplayType";
import { observer } from "mobx-react-lite";
import React from "react";
import { formatDate, formatDateTime, formatPhoneNumber } from "utils/localizations";
import ChargeHistoriesViewModel from "viewModels/callCenter/search/charges/chargeDetail/chargeHistoriesViewModel";
import ChargeChangeOrigin from "entities/chargeChangeOrigin";
import { emptyValue } from "utils/emptyValue";
import type VehicleRegisterVehicleChange from "entities/vehicleRegisterVehicleChange";

// eslint-disable-next-line sonarjs/cognitive-complexity
const ChargeHistoriesView: ViewComponent<ChargeHistoriesViewModel> = observer(({ vm }) => {
  if (!vm.charge) {
    return null;
  }

  function changeValueFormatter(item: ChargeChange, type: "new" | "original") {
    const value = type === "new" ? item.newValue : item.originalValue;
    const translateValue = vm.localizationService.translateEnum(
      "chargePropertyChangeDisplayType",
      value as ChargePropertyChangeDisplayType
    );
    if (!value) {
      return emptyValue;
    }
    if (!translateValue.includes(".")) {
      return translateValue;
    }

    switch (item.changeType) {
      case ChargePropertyChangeDisplayType.FuelTypeChange:
      case ChargePropertyChangeDisplayType.FuelTypeChangeWithPartialRefund:
        return vm.translateFuelType(value);

      case ChargePropertyChangeDisplayType.VehicleRegisterVehicleIdChange:
      case ChargePropertyChangeDisplayType.VehicleRegisterVehiclePropertiesChange:
        return emptyValue;

      case ChargePropertyChangeDisplayType.CountryChangeBeforeValidity:
      case ChargePropertyChangeDisplayType.CountryChangeDuringValidity:
      case ChargePropertyChangeDisplayType.CountryChangeInDistributorLimit:
      case ChargePropertyChangeDisplayType.SingleCountryChange:
      case ChargePropertyChangeDisplayType.BulkCountryChange:
        return vm.translateCountry(value);

      case ChargePropertyChangeDisplayType.EmailNotificationChange:
      case ChargePropertyChangeDisplayType.SmsNotificationChange:
        return vm.translateBoolean(value);

      case ChargePropertyChangeDisplayType.ValidSinceChangeBeforeValidity:
      case ChargePropertyChangeDisplayType.ValidSinceChangeInDistributorLimit:
        return <span className="text-nowrap">{formatDateTime(new Date(value))}</span>;

      case ChargePropertyChangeDisplayType.NotificationPhoneNumberChange:
        return formatPhoneNumber(value);

      default:
        return value;
    }
  }

  function changeVehicleValueFormatter(key: string, value: any) {
    let formatedValue = emptyValue;

    switch (key) {
      case "updatedAt":
        formatedValue = value ? formatDateTime(new Date(value)) : emptyValue;
        break;
      case "licensePlateValidSinceDate":
      case "licensePlateValidUntilDate":
      case "vehicleStatusValidSinceDate":
      case "vehicleStatusValidUntilDate":
        formatedValue = value ? formatDate(new Date(value)) : emptyValue;
        break;
      case "fuelTypes":
        formatedValue = value ? vm.translateVehicleRegisterVehicleFuelType(value) : emptyValue;
        break;
      case "maximumAllowedWeight":
        formatedValue = value ? `${value} kg` : emptyValue;
        break;
      case "classifiedFuelType":
        formatedValue = value ? vm.translateVehicleRegisterVehicleClassifiedFuelType(value) : emptyValue;
        break;
      case "vehicleStatus":
        formatedValue = value ? vm.translateVehicleStatus(value) : emptyValue;
        break;
      default:
        formatedValue = value ?? emptyValue;
        break;
    }

    return formatedValue.toString();
  }

  return (
    <>
      <LoaderOverlay loading={vm.busyWatcher.isBusy}>
        <table className="table table-full table-tbody-striped tbody-larger">
          <thead>
            <tr>
              <th>{vm.translateAttribute("changedAt")}</th>
              <th>{vm.translateChangeAttribute("changeType")}</th>
              <th>{vm.translateChangeAttribute("originalValue")}</th>
              <th style={{ width: 70 }}></th>
              <th>{vm.translateChangeAttribute("newValue")}</th>
              <th>{vm.translateAttribute("changeInitiator")}</th>
              <th>{vm.translateAttribute("changeOrigin")}</th>
              <th>{vm.translateAttribute("officialRecordId")}</th>
              <th></th>
            </tr>
          </thead>
          <>
            {vm.transformedItems.map((item, indexItem) => {
              const className = item.wasMadeByCustomerDuringValidity ? "text-danger" : "text-primary";
              const detailRSV: JSX.Element[] = [];
              const showDetail = vm.canShowDetail(item);

              if (item.newVehicleRegisterVehicle || item.originalVehicleRegisterVehicle) {
                let obj = item.originalVehicleRegisterVehicle ? item.originalVehicleRegisterVehicle : undefined;
                if (!obj) {
                  obj = item.newVehicleRegisterVehicle;
                }
                obj &&
                  Object.keys(obj).forEach((key: string, indexDetail) => {
                    const originalValue = item.originalVehicleRegisterVehicle
                      ? item.originalVehicleRegisterVehicle[key as keyof VehicleRegisterVehicleChange]
                      : undefined;
                    const updatedValue = item.newVehicleRegisterVehicle
                      ? item.newVehicleRegisterVehicle[key as keyof VehicleRegisterVehicleChange]
                      : undefined;
                    if (key === "updatedAt") {
                      return;
                    }
                    const originalString = originalValue ? changeVehicleValueFormatter(key, originalValue) : emptyValue;
                    const updatedString = updatedValue ? changeVehicleValueFormatter(key, updatedValue) : emptyValue;
                    const isDiff =
                      originalString !== updatedString &&
                      item.changeType === ChargePropertyChangeDisplayType.VehicleRegisterVehiclePropertiesChange;

                    detailRSV.push(
                      <tr
                        key={`${item.changedAt}-${key}`}
                        className={`tr-narrow ${item._showDetail ? "show-detail" : "hidden-detail"} ${
                          (indexItem + 1 + indexDetail + 1) % 2 === 0 ? "even" : "odd"
                        } ${isDiff ? "diff" : ""}`}>
                        <td></td>
                        <td>{vm.translateAttribute(key as PropertyName<ChargeChange>)}</td>
                        <td>{originalValue ? changeVehicleValueFormatter(key, originalValue) : emptyValue}</td>
                        <td>→</td>
                        <td>{updatedValue ? changeVehicleValueFormatter(key, updatedValue) : emptyValue}</td>
                        <td></td>
                        <td></td>
                        <td></td>
                        <td></td>
                      </tr>
                    );
                  });
              }
              return (
                <>
                  <tbody key={`${item.changedAt}-${item.changeOrigin}`}>
                    <tr key={`${item.changedAt}-${item.changeType}`} className={`${(indexItem + 1) % 2 === 0 ? "even" : "odd"}`}>
                      <td>{formatDateTime(item.changedAt)}</td>
                      <td>
                        <strong className={className}>{vm.translateChangedChargeProperty(item, item.changeType)}</strong>
                      </td>
                      <td>{changeValueFormatter(item, "original")}</td>
                      <td>→</td>
                      <td>{changeValueFormatter(item, "new")}</td>
                      <td>
                        {item.changeOrigin === ChargeChangeOrigin.BackOffice
                          ? item.changedBy
                          : vm.translateChargeChangeInitiator(item.changeInitiator)}
                      </td>
                      <td>{vm.translateChargeChangeOrigin(item.changeOrigin)}</td>
                      <td>{item.changeOrigin === ChargeChangeOrigin.BackOffice ? item.officialRecordId : emptyValue}</td>
                      <td>
                        {showDetail && (
                          <Button
                            onClick={() => vm.handleShowDetail(item)}
                            variant="light"
                            size="sm"
                            iconRight={item._showDetail ? IconType.ArrowTop : IconType.ArrowBottom}
                          />
                        )}
                      </td>
                    </tr>
                    <>
                      {showDetail &&
                        detailRSV?.map((item: any) => {
                          return item;
                        })}
                    </>
                  </tbody>
                </>
              );
            })}
          </>
        </table>
        {vm.items?.length === 0 && (
          <div className="bg-white mt-n2 mb-4 px-4 py-3 border-bottom text-muted full-width">
            {vm.translateGeneral(vm.busyWatcher.isBusy ? "loading" : "empty")}
          </div>
        )}
      </LoaderOverlay>
    </>
  );
});

registerView(ChargeHistoriesView, ChargeHistoriesViewModel);
